import React, {
  useCallback, useEffect, useMemo,
} from 'react';

import PropTypes from 'prop-types';
import { TextField } from '@picsio/ui';

import l10n from '../../../../shared/strings';
import productStyles from '../../styles.module.scss';
import { useBlurCommit, useEnterToBlur, useLocalFieldValue } from '../../hooks';
import {
  normalizeLinkValue,
  serializeLinkValue,
} from '../utils';
import styles from '../fields.module.scss';
import ProductFieldRow from './ProductFieldRow';
import ProductFieldLabel from './ProductFieldLabel';

const LinkField = ({
  data, value, disabled, isDiff, onChange, textPlaceholder, validate, savedError, onValidationChange,
}) => {
  const normalized = useMemo(() => normalizeLinkValue(value), [value]);
  const { localValue, setLocalValue } = useLocalFieldValue(normalized);

  useEffect(() => {
    setLocalValue(normalizeLinkValue(value));
  }, [setLocalValue, value]);

  const commit = useCallback((nextValue, onCancel) => {
    onChange(serializeLinkValue(nextValue), data, onCancel);
  }, [data, onChange]);

  const { handleBlur, error: validationError, clearErrorIfValid } = useBlurCommit({
    value: normalized,
    localValue,
    setLocalValue,
    onCommit: commit,
    serialize: serializeLinkValue,
    validate,
    savedError,
    onValidationChange,
  });

  const handleTitleChange = useCallback((event) => {
    const nextValue = { ...localValue, title: event.target.value };
    setLocalValue(nextValue);
    clearErrorIfValid(nextValue);
  }, [clearErrorIfValid, localValue, setLocalValue]);

  const handleUrlChange = useCallback((event) => {
    const nextValue = { ...localValue, url: event.target.value };
    setLocalValue(nextValue);
    clearErrorIfValid(nextValue);
  }, [clearErrorIfValid, localValue, setLocalValue]);

  const handleKeyPress = useEnterToBlur();

  return (
    <ProductFieldRow title={data.title} className={productStyles.row}>
      <div className={styles.mediaField}>
        <TextField
          value={localValue.title}
          label={(
            <ProductFieldLabel
              title={data.title}
              tooltip={l10n.PRODUCT_INFO.fieldHelpers.link}
              isDiff={isDiff}
            />
          )}
          disabled={disabled}
          placeholder={textPlaceholder}
          onChange={handleTitleChange}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
        />
        <TextField
          value={localValue.url}
          disabled={disabled}
          placeholder={l10n.PRODUCT_INFO.placeholders.linkUrl}
          error={Boolean(validationError)}
          helperText={validationError}
          onChange={handleUrlChange}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
        />
      </div>
    </ProductFieldRow>
  );
};

LinkField.propTypes = {
  data: PropTypes.shape({ title: PropTypes.string }).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  disabled: PropTypes.bool,
  isDiff: PropTypes.bool,
  textPlaceholder: PropTypes.string,
  validate: PropTypes.func,
  savedError: PropTypes.string,
  onValidationChange: PropTypes.func,
  onChange: PropTypes.func.isRequired,
};

LinkField.defaultProps = {
  value: '',
  disabled: false,
  isDiff: false,
  textPlaceholder: '',
  validate: null,
  savedError: null,
  onValidationChange: null,
};

export default LinkField;
